import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAudit } from '../context/AuditContext';
import { auditPath } from '../utils/auditRoutes';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getBackendUrl } from '../utils/apiBase';

const BACKEND_URL = getBackendUrl();

export default function ResumePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { setAuditData } = useAudit();

  const [status, setStatus] = useState('loading'); // loading | success | error
  const [message, setMessage] = useState('');

  const token = searchParams.get('token');

  useEffect(() => {
    if (token) {
      resumeAudit();
    } else {
      setStatus('error');
      setMessage('No resume token provided');
    }
  }, [token]);

  const resumeAudit = async () => {
    try {
      setStatus('loading');

      const response = await fetch(`${BACKEND_URL}/api/audit/resume-data?token=${encodeURIComponent(token)}`);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to resume audit');
      }

      const data = await response.json();

      // Check if audit is completed
      if (data.completed && data.audit_id) {
        // Audit already completed - redirect to report
        setStatus('success');
        setMessage('Your audit is complete! Redirecting to your report...');
        setTimeout(() => {
          navigate(auditPath(`/report?audit_id=${data.audit_id}`, location.pathname));
        }, 1500);
        return;
      }

      // Restore audit data to context (mid-progress)
      setAuditData(data.audit_data);

      setStatus('success');
      setMessage('Your audit has been restored!');

      // Navigate to first incomplete page or welcome page
      setTimeout(() => {
        navigate(auditPath('/welcome', location.pathname));
        window.scrollTo(0, 0);
      }, 1500);

    } catch (err) {
      console.error('Error resuming audit:', err);
      setStatus('error');
      setMessage(err.message || 'Failed to resume audit. Link may be expired.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary to-secondary flex items-center justify-center p-6">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="h-16 w-16 animate-spin text-primary mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-primary mb-2">Restoring Your Audit...</h2>
            <p className="text-muted-foreground">Please wait while we load your saved responses.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="h-16 w-16 text-accent mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-primary mb-2">Welcome Back!</h2>
            <p className="text-muted-foreground mb-4">{message}</p>
            <p className="text-sm text-muted-foreground">Redirecting you to your audit...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <AlertCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-destructive mb-2">Unable to Resume</h2>
            <p className="text-muted-foreground mb-6">{message}</p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate(auditPath('/checkout', location.pathname))}
                className="w-full"
              >
                Start New Audit
              </Button>
              <p className="text-xs text-muted-foreground">
                Need help? Email <a href="mailto:hello@trilogyoneconsulting.com" className="text-primary hover:underline">hello@trilogyoneconsulting.com</a>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
